import type { TimeRangeConfig } from '@/api/generated/model';
import { TimeRangeKind } from '@/api/generated/model';
import { formatTimeRange } from '@/comps/date-picker-button';
import { TIME_RANGE_LABELS } from '@/comps/component-library/reports/TimeRangeSelectPanel';
import { getDateTRangeFromKind } from '@/utils/get-date-range-from-kind';
import { formatToLocaleDateValue, getTimezoneName } from '@/utils/date.util';
import dayjs from 'dayjs';
import { endOfDay, startOfDay } from 'date-fns';

function snapResolvedTimeRangeToDayBounds(range: TimeRangeConfig): TimeRangeConfig {
  const start = dayjs(range.start);
  const end = dayjs(range.end);

  if (!start.isValid() || !end.isValid()) {
    return range;
  }

  return {
    ...range,
    start: formatToLocaleDateValue(startOfDay(start.toDate()).getTime()),
    end: formatToLocaleDateValue(endOfDay(end.toDate()).getTime()),
  };
}

export function resolveNebulyPageTimeRange(
  timeRange: TimeRangeConfig | undefined,
  now: Date = new Date(),
  effectiveNow?: Date,
): TimeRangeConfig | undefined {
  if (!timeRange) return undefined;

  const timezone = timeRange.timezone ?? getTimezoneName();
  const recompute = timeRange.recompute ?? true;
  const isCustom = timeRange.kind === TimeRangeKind.custom;
  const customResolutionNow = isCustom && effectiveNow ? effectiveNow : now;
  const hasResolvedBounds =
    Boolean(timeRange.start) &&
    Boolean(timeRange.end) &&
    dayjs(timeRange.start).isValid() &&
    dayjs(timeRange.end).isValid();

  const resolved = hasResolvedBounds
    ? {
        kind: timeRange.kind,
        start: timeRange.start,
        end:
          isCustom && recompute && effectiveNow
            ? formatToLocaleDateValue(endOfDay(effectiveNow).getTime())
            : timeRange.end,
        timezone,
        recompute,
      }
    : (() => {
        const fromKind = getDateTRangeFromKind(
          {
            kind: timeRange.kind,
            start: timeRange.start ?? '',
            end: timeRange.end ?? '',
            timezone,
            recompute,
          },
          customResolutionNow,
        );

        if (isCustom && recompute && fromKind.start && !fromKind.end) {
          return {
            ...fromKind,
            end: formatToLocaleDateValue(endOfDay(customResolutionNow).getTime()),
          };
        }

        return fromKind;
      })();

  return snapResolvedTimeRangeToDayBounds({
    ...resolved,
    timezone,
    recompute,
  });
}

/**
 * Reads the picker's label as a sentence fragment, so a KPI can say what period it
 * covers: "in the last 6M", "today", "in the last month". Rolling windows get "the
 * last" in front; named periods ("This month", "Year to date") already read as one.
 */
function toTimeRangePhrase(label: string): string {
  const lower = label.toLowerCase();

  if (lower === 'today' || lower === 'yesterday' || lower === 'year to date') return lower;
  // Short rolling buckets, e.g. 7D, 30D, 3M.
  if (/^\d+[dmy]$/.test(lower)) return `in the last ${label}`;
  if (lower.startsWith('last ') || lower.endsWith('quarter')) return `in the ${lower}`;

  return `in ${lower}`;
}

/** Period phrase for a KPI headline, e.g. "in the last 6M". */
export function getPageTimeRangePhrase(timeRange: TimeRangeConfig | undefined): string {
  if (!timeRange) return 'in the selected period';

  const label = TIME_RANGE_LABELS[timeRange.kind];
  return label ? toTimeRangePhrase(label) : `in ${formatTimeRange(timeRange)}`;
}
